// packages/utils/src/shared/apiRequestOutcome.ts
//
// The Result an HTTP call answers with, for the repo's two clients of one contract: `apiRequest` (axios: the
// server's adapters, with mTLS agents, XML bodies and retries) and `fetchApiRequest` (the platform's `fetch`: the
// browser widgets, whose bundles have a size budget). Each outcome is defined once, so a caller reads the same
// kinds and codes whichever client made the call.
import type { Result } from '@tourtask/types/result';
import { failure, success } from '@tourtask/utils/shared/result';
import { isResultEnvelope } from '@tourtask/utils/shared/resultHttp';

/** `result`: the endpoint answers with our `Result<T>` envelope; `raw`: the caller takes the body as it came. */
export type ApiRequestExpect = 'result' | 'raw';

/**
 * A response's parsed body as the caller's Result, whatever its HTTP status (the body decides): the body itself
 * (`raw`), the endpoint's own envelope, or `HTTP.UNEXPECTED_RESPONSE` for any other body (a gateway's or proxy's
 * error page, an empty answer).
 */
export function resultOfResponseBody<T>(data: unknown, status: number, expect: ApiRequestExpect): Result<T> {
  if (expect === 'raw') return success(data as T);
  if (isResultEnvelope<T>(data)) return data;
  return failure({
    kind: 'UNKNOWN',
    code: 'HTTP.UNEXPECTED_RESPONSE',
    message: 'Unexpected response format',
    details: { status, data },
  });
}

/** No answer within the request's time limit (or the request was aborted before one came). */
export function requestTimeoutFailure(timeoutMs: number, url: string, method: string): Result<never> {
  return failure({
    kind: 'TIMEOUT',
    code: 'HTTP.TIMEOUT',
    message: 'Request timed out',
    details: { timeoutMs, url, method },
  });
}

/**
 * No response at all: DNS failure, connection refused, server unreachable, a CORS refusal. CORS-rejected API calls
 * rarely land here: the API echoes CORS headers on its errors (`CORS.ORIGIN_NOT_ALLOWED` included), so those come
 * back as the endpoint's own Result.
 */
export function requestNetworkFailure(url: string, method: string): Result<never> {
  return failure({
    kind: 'OFFLINE',
    code: 'HTTP.NETWORK',
    message: 'Network error',
    details: { url, method },
  });
}
